import {
	defaultAlertConfigs,
	defaultCitationsConfig,
	defaultDeploymentsConfig,
	defaultExamConfig,
	defaultFont,
	defaultLeaderboardsConfig,
	defaultMaintenanceConfig,
	defaultPracticeTestConfig,
	defaultSessionViewConfig,
} from "@configs/custom-app-config-defaults";
import { defaultAiAssistantInfo } from "@memorang/helpers/constants";
import type { Apps } from "types/apps";
import { z } from "zod/v4";

export const LOCALES = ["en-US", "zh-CN", "ja-JP"] as const;

export type Locale = (typeof LOCALES)[number];
const tenantSchema = z.object({
	id: z.string(),
	name: z.string(),
});

const internalizationSchema = z.record(z.string(), z.string());

const masteryLabelsSchema = z.object({
	low: z.string(),
	medium: z.string(),
	high: z.string(),
	none: z.string(),
});

const consentSchema = z.object({
	text: internalizationSchema.optional(),
});

const landingSchema = z.object({
	title: z
		.string()
		.or(
			z
				.object({
					prefix: z.string(),
					decoratedText: z.string(),
					suffix: z.string(),
				})
				.partial(),
		)
		.optional(),
	marketText: z
		.object({
			prefix: z.string().optional(),
			suffix: z.string().optional(),
		})
		.optional(),
	subtitle: z.string().optional(),
	valueProps: z
		.array(
			z.object({
				icon: z.string(),
				title: z.string(),
				subtitle: z.string(),
			}),
		)
		.optional(),
	landingGraphics: z.string().optional(),
	consent: consentSchema.optional(),
});

const scoreRangeSchema = z.object({
	rangeArray: z.array(z.number()),
	range: z.string(),
	color: z.string(),
	icon: z.string(),
});

const fontSizesSchema = z.object({
	stem: z.number(),
	answer: z.number(),
});

const purchaseSchema = z.object({
	isSubscriptionEnabled: z.boolean().optional(),
	subheading: z.string().nullable().optional(),
	baseSku: z.string().optional(),
	paywall: z
		.object({
			title: z.string().optional(),
			subtitle: z.string().optional(),
			cta: z.string().optional(),
			disclaimer: z.string().optional(),
		})
		.optional(),
});

export const logoSchema = z.object({
	icon: z.string().optional(),
	adaptiveIcon: z.string().optional(),
	splashBackgroundColor: z.string().optional(),
	splashImageWidth: z.number().optional(),
	adaptiveIconBackgroundColor: z.string().optional(),

	stacked: z.object({
		// todo: might be more complex schema for image
		light: z.object({
			uri: z.string(),
		}),
		dark: z.object({
			uri: z.string(),
		}),
	}),
	default: z.object({
		light: z.object({
			uri: z.string(),
		}),
		dark: z.object({
			uri: z.string(),
		}),
	}),
	company: z
		.object({
			light: z.object({
				uri: z.string(),
			}),
			dark: z.object({
				uri: z.string(),
			}),
		})
		.optional(),
});

export type LogoConfigSchemaType = z.infer<typeof logoSchema>;

export const linksSchema = z.object({
	baseUrl: z.string().optional(),
	privacy: z.string(),
	tos: z.string(),
	ios: z.string().optional(),
	aiToolsTos: z.string().optional(),
	android: z.string().optional(),
	redeemSupportUrl: z.string().optional(),
	companyUrl: z.string().optional(),
});

export type LinksConfigSchemaType = z.infer<typeof linksSchema>;

const revenueCatSchema = z.object({
	ios: z.string(),
	android: z.string(),
});

const appAnalyticsSchema = z.object({
	mixpanel: z.object({
		id: z.string(),
	}),
	analyticsDestinations: z
		.array(
			z.object({
				type: z.enum(["mixpanel", "segment"]),
				apiKey: z.string(),
				name: z.string().optional(),
				projectId: z.string().optional(),
			}),
		)
		.optional(),
});

const authProviderSchema = z.enum(["clerk", "supabase"]).default("clerk");

export type AuthProvider = z.infer<typeof authProviderSchema>;

const clerkSchema = z.object({
	publishableKeyDev: z.string(),
	publishableKeyProd: z.string(),
});

const supabaseAuthEnvSchema = z.object({
	url: z.string(),
	anonKey: z.string(),
	redirectUrl: z.string().optional(),
});

const supabaseAuthSchema = z.object({
	dev: supabaseAuthEnvSchema,
	prod: supabaseAuthEnvSchema,
});

export type SupabaseAuthEnvConfig = z.infer<typeof supabaseAuthEnvSchema>;
export type SupabaseAuthConfig = z.infer<typeof supabaseAuthSchema>;

const appKeysSchema = z.object({
	revenueCat: revenueCatSchema,
	oneSignalAppId: z.string(),
	clerk: clerkSchema.optional().nullable(),
	supabase: supabaseAuthSchema.optional().nullable(),
	airtableBase: z.string().optional(),
});

const integrationsSchema = z.object({
	slack: z
		.object({
			channel: z.string().optional(),
		})
		.optional(),
});

const sessionConfigFiltersSchema = z.array(
	z.object({
		label: internalizationSchema,
		distKey: z.string(),
		color: z.string().optional(),
	}),
);

export type SessionConfigFiltersType = z.infer<
	typeof sessionConfigFiltersSchema
>;

const sessionConfigSchema = z.object({
	showDistributionFilters: z.boolean().optional(),
	maxItemsPerSession: z.number().optional(),
	showItemsSlider: z.boolean().optional(),
	showLearningMode: z.boolean().optional(),
	filters: sessionConfigFiltersSchema.optional(),
	crossTagSelection: z.boolean().optional(),
});

const welcomeDialogSchema = z.object({
	diagnostic: z.object({
		title: z.string(),
		body: z.string(),
	}),
	disclaimer: z.string().optional(),
});

export type WelcomeDialogSchemaType = z.infer<typeof welcomeDialogSchema>;
const sessionViewConfigSchema = z.object({
	cta: z.string().optional(),
	disclaimer: z.string().optional(),
	enableSessionReplay: z.boolean().optional(),
	welcomeDialog: welcomeDialogSchema.optional(),
	showFlightCalculator: z
		.boolean()
		.optional()
		.default(defaultSessionViewConfig.showFlightCalculator),
	maxItems: z
		.object({
			flashcards: z.number(),
			questions: z.number(),
		})
		.optional(),
	postAnswerStats: z
		.object({
			showXpEarned: z.boolean().optional(),
			showNumAttempts: z.boolean().optional(),
		})
		.optional(),
	timer: z.boolean().optional(),
	showCrossOut: z
		.boolean()
		.optional()
		.default(defaultSessionViewConfig.showCrossOut),
	selectionStrategy: z
		.object({
			default: z.string(),
			optimized: z.boolean(),
			random: z.boolean(),
			shuffled: z.boolean(),
		})
		.optional(),
	suggestCorrection: z
		.object({
			description: z.string(),
		})
		.optional(),
});

const statsSchema = z.array(
	z.object({
		type: z.string(),
		title: z.string(),
		icon: z.string(),
		tooltip: z.string().optional(),
	}),
);

export type AppConfigStatsType = z.infer<typeof statsSchema>;

const cardSchema = z.object({
	type: z.string(),
	title: internalizationSchema.optional(),
});

export type CardSchemaType = z.infer<typeof cardSchema>;

const cardsSchema = z.array(cardSchema);

export type AppConfigCardsType = z.infer<typeof cardsSchema>;

export const faqItemSchema = z.object({
	question: z.string().trim(),
	answer: z.string().trim(),
	locale: z.enum(LOCALES),
	order: z.number().min(0),
});
export type FaqItemSchemaType = z.infer<typeof faqItemSchema>;
const accountSettingsViewSchema = z.object({
	showFaqs: z.boolean().optional(),
	showPurchasedProducts: z.boolean().optional(),
	showCurrentExam: z.boolean().optional(),
	faqs: z.array(faqItemSchema).optional(),
	showLanguageSettings: z.boolean().optional(),
	showAudioTestDialog: z.boolean().optional(),
	showPartnersSection: z.boolean().optional(),
});

const reportViewConfigSchema = z.object({
	practiceTest: z.object({
		reviewEnabled: z.boolean(),
	}),
	stats: statsSchema.optional(),
	cards: cardsSchema.optional(),
	formative: z
		.object({
			showPointsBasedScore: z.boolean().optional(),
			maxScore: z.number().optional(),
			reviewEnabled: z.boolean().optional(),
		})
		.optional(),
});

const onBoardingStepTypeEnum = z.enum([
	"privacy",
	"chooseCategory",
	"chooseLevel",
	"chooseState",
	"chooseContext",
	"chooseExam",
	"examDate",
	"set-date",
	"chooseDate",
	"role",
	"specialty",
	"avatar",
	"country",
	"name",
	"welcome",
	"features",
	"demo",
	"skills",
	"set-goal",
]);

export type OnboardingStepType = z.infer<typeof onBoardingStepTypeEnum>;

const onboardingDataItemSchema = z.object({
	title: z.string().optional(),
	description: z.string().optional(),
	descriptioni18n: internalizationSchema.optional(),
	titlei18n: internalizationSchema.optional(),
	icon: z.string().optional(),
	type: z.string().optional(),
	image: z.string().optional(),
});

export type OnboardingDataItemType = z.infer<typeof onboardingDataItemSchema>;

const onboardingStepSchema = z.object({
	step: onBoardingStepTypeEnum,
	enabled: z.boolean(),
	title: z.string(),
	titlei18n: internalizationSchema.optional(),
	description: z.string().optional(),
	descriptioni18n: internalizationSchema.optional(),
	isMobile: z.boolean().optional(),
	cta: z.string().optional(),
	graphics: z
		.object({
			desktop: z.string(),
			tablet: z.string().optional(),
			mobile: z.string().optional(),
		})
		.optional(),
	ctai18n: internalizationSchema.optional(),
	data: z.array(onboardingDataItemSchema).optional(),
});

export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

const navigationRouteSchema = z.object({
	title: z.string(),
	name: z.string(),
	icon: z.object({
		default: z.string(),
		selected: z.string(),
	}),
	enabled: z.boolean(),
	custom: z.boolean().optional(),
});

const navigationSchema = z.object({
	routes: z.object({
		main: z.array(navigationRouteSchema),
	}),
});

const oldPortalSchema = z.object({
	title: z.string(),
	description: z.string(),
	link: z.string(),
});

const aiAssistantSchema = z.object({
	title: z.string().optional().default(defaultAiAssistantInfo.title),
	alias: z.string().optional().default(defaultAiAssistantInfo.alias),
	icon: z.string(),
	disclaimer: z
		.object({
			enabled: z.boolean(),
			title: z.string().optional().default("Disclaimer"),
			body: z
				.string()
				.optional()
				.default(
					"**{{agentName}}** can make mistakes. Use it at your own discretion and verify important information.",
				),
		})
		.optional(),
	showOriginalQuestion: z
		.boolean()
		.optional()
		.default(defaultAiAssistantInfo.showOriginalQuestion),
	initialMessage: z
		.string()
		.optional()
		.default(defaultAiAssistantInfo.initialMessage),
	initialAiMessage: internalizationSchema.optional(),
});

const masteryLevelsSchema = z.array(
	z.object({
		id: z.string(),
		darkColor: z.string(),
		lightColor: z.string(),
		sortOrder: z.number(),
		icon: z.string(),
		range: z.object({
			type: z.string(),
			score: z.array(z.number()),
			points: z.array(z.number()),
		}),
		alias: z.object({
			title: z.string(),
		}),
		level: z.string(),
	}),
);

export type AppConfigMasteryLevelsType = z.infer<typeof masteryLevelsSchema>;

const fontSchema = z.enum([
	"opensans",
	"beausite",
	"greetnarrow",
	"greetstandard",
	"sourcesanspro",
	"calibri",
	"cambridgesans",
	"notoserif",
	"plusjakartasans",
]);

export type FontSchemaType = z.infer<typeof fontSchema>;

const citationsSchema = z.object({
	visible: z.boolean().optional().default(defaultCitationsConfig.visible),
	showTextBasedCitationOnly: z
		.boolean()
		.optional()
		.default(defaultCitationsConfig.showTextBasedCitationOnly),
});

const deploymentInfoSchema = z.object({
	id: z.string(),
	deployedAt: z.string().optional(),
	version: z.string().optional(),
});

const deploymentsSchema = z
	.object({
		autoDeploymentEnabled: z
			.boolean()
			.optional()
			.default(defaultDeploymentsConfig.autoDeploymentEnabled),
		web: z
			.object({
				pendingDeployment: deploymentInfoSchema.optional().nullable(),
				lastDeployment: deploymentInfoSchema.optional().nullable(),
			})
			.optional()
			.nullable(),
	})
	.optional();

const leaderboardsConfigSchema = z.object({
	timePeriods: z
		.array(z.enum(["all_time", "month", "week"]))
		.optional()
		.default([...defaultLeaderboardsConfig.timePeriods]),
	scopes: z
		.array(z.enum(["global", "friends"]))
		.optional()
		.default([...defaultLeaderboardsConfig.scopes]),
	showUsersPodium: z
		.boolean()
		.optional()
		.default(defaultLeaderboardsConfig.showUsersPodium),
	numUsersToDisplay: z
		.number()
		.min(3)
		.max(25)
		.optional()
		.default(defaultLeaderboardsConfig.numUsersToDisplay),
});

const iosBuildNumberStateSchema = z.object({
	currentVersion: z.string(),
	buildNumber: z.number(),
});

export type IosBuildNumberStateSchemaType = z.infer<
	typeof iosBuildNumberStateSchema
>;

export const mobileAndroidSchema = z.object({
	versionCode: z.number().optional(),
	package: z.string().optional(),
	googleServicesJson: z.string().nullable().optional(),
	serviceAccountJson: z
		.string()
		.or(z.record(z.string(), z.unknown()))
		.nullable()
		.optional(),
});

export type MobileAndroidSchemaType = z.infer<typeof mobileAndroidSchema>;

export const mobileIosSchema = z.object({
	bundleIdentifier: z.string().optional(),
	buildNumberState: iosBuildNumberStateSchema.nullable().optional(),
});

export type MobileIosSchemaType = z.infer<typeof mobileIosSchema>;

export const appConfigSchema = z.object({
	id: z.string(),
	name: z.string(),
	scheme: z.string().optional(),
	auth: z
		.object({
			provider: authProviderSchema,
		})
		.optional()
		.default({ provider: "clerk" }),
	enableMediaPlayback: z.boolean().optional(),
	mobile: z
		.object({
			apple: z
				.object({
					teamId: z.string().optional(),
					ascIssuerId: z.string().optional(),
					ascKeyId: z.string().optional(),
					ascApiKeyP8: z.string().optional(),
					ascAppId: z.string().optional(),
				})
				.optional(),
			android: mobileAndroidSchema.optional(),
			ios: mobileIosSchema.optional(),
		})
		.optional(),
	version: z.string().optional(),
	maintenance: z
		.object({
			enabled: z.boolean(),
			title: z.string().optional().default(defaultMaintenanceConfig.title),
			urls: z
				.object({
					ios: z.string().optional(),
					android: z.string().optional(),
				})
				.optional(),
			subtitle: z
				.string()
				.optional()
				.default(defaultMaintenanceConfig.subtitle),
		})
		.optional(),

	font: fontSchema.optional().default(defaultFont),
	trademark: z.string().optional(),
	showDiagnostic: z.boolean().optional(),
	showPracticeTests: z.boolean().optional(),
	purchase: purchaseSchema.optional(),
	masteryLabels: masteryLabelsSchema.optional(),
	masteryLevels: masteryLevelsSchema.optional(),
	scoreRanges: z.array(scoreRangeSchema).optional(),
	fontSizes: fontSizesSchema.optional(),
	componentOverrides: z.record(z.string(), z.any()).optional(),
	analytics: appAnalyticsSchema,
	logo: logoSchema,
	links: linksSchema,
	keys: appKeysSchema,
	integrations: integrationsSchema.optional(),
	supportUrl: z.string().optional(),
	tagType: internalizationSchema.optional().default({
		"en-US": "Topic",
	}),
	oldPortal: oldPortalSchema.optional(),
	aiAssistant: aiAssistantSchema.optional().default(defaultAiAssistantInfo),
	isImageChallengeApp: z.boolean().optional().default(false),
	isStudyPackEnabled: z.boolean().optional().default(true),
	landing: landingSchema.optional(),
	showCountrySelection: z.boolean().optional().default(false),
	consent: consentSchema.optional(),
	avatarType: z.enum(["initials", "identicon"]).optional(),
	sessionConfig: sessionConfigSchema.optional(),
	accountSettings: accountSettingsViewSchema.optional().nullable(),
	appAvailabilityRegions: z
		.array(z.string())
		.optional()
		.describe(
			"Array of ISO 3166-1 alpha-2 country codes where app is available. Empty or undefined means available everywhere",
		),
	citations: citationsSchema.optional(),
	deployments: deploymentsSchema.optional().default(defaultDeploymentsConfig),
	leaderboards: leaderboardsConfigSchema.optional().default({
		...defaultLeaderboardsConfig,
		timePeriods: [...defaultLeaderboardsConfig.timePeriods],
		scopes: [...defaultLeaderboardsConfig.scopes],
	}),
});

export type AppConfigSchemaType = z.infer<typeof appConfigSchema>;

export type DeploymentInfoSchemaType = z.infer<typeof deploymentInfoSchema>;
export type DeploymentsSchemaType = z.infer<typeof deploymentsSchema>;

const themeSchema = z.record(z.string(), z.any());

const alertItemSchema = z.object({
	description: z.string(),
	icon: z.string(),
	severity: z.enum(["info", "warning", "error"]),
	title: z.string().optional(),
});

const alertConfigSchema = z.object({
	quiz: alertItemSchema.default(defaultAlertConfigs.quiz),
	practiceTest: alertItemSchema.default(defaultAlertConfigs.practiceTest),
	quizAndPracticeTest: alertItemSchema.default(
		defaultAlertConfigs.quizAndPracticeTest,
	),
	topicMastery: alertItemSchema.default(defaultAlertConfigs.topicMastery),
});

const summativeTestAlertConfigSchema = alertConfigSchema
	.optional()
	.default(defaultAlertConfigs);

export type SummativeTestAlertConfigType = z.infer<typeof alertConfigSchema>;

const practiceTestConfigSchema = z
	.object({
		header: z
			.object({
				title: z.string(),
				subtitle: z.string(),
			})
			.optional()
			.default(defaultPracticeTestConfig.header),
		label: z.string().optional().default(defaultPracticeTestConfig.label),
		summativeTestAlertConfig: summativeTestAlertConfigSchema,
		enabled: z
			.object({
				mobile: z.boolean().default(defaultPracticeTestConfig.enabled.mobile),
				web: z.boolean().default(defaultPracticeTestConfig.enabled.web),
			})
			.optional()
			.default(defaultPracticeTestConfig.enabled),
		deviceRecommendationDialog: z
			.object({
				enabled: z.boolean(),
				title: z.string(),
				message: z.string(),
			})
			.optional()
			.default(defaultPracticeTestConfig.deviceRecommendationDialog),
	})
	.optional();

export const customConfigTypeSchema = z.object({
	tenant: tenantSchema,
	supportEmail: z.string(),
	companyName: z.string(),
	app: appConfigSchema,
	groupByCategory: z.boolean().optional().nullable(),
	exam: z.object({
		enabled: z.boolean(),
		whiteListedExamIds: z.array(z.string()).optional(),
		quizEnabled: z.boolean().optional(),
		allowChangeExam: z.boolean().optional(),
		label: z.string().optional(),
		diagnosticLabel: z.record(z.string(), z.string()).optional(),
		taskCardSubtitle: z.string().optional(),
		practiceTest: practiceTestConfigSchema
			.optional()
			.default(defaultExamConfig.practiceTest),
		examDateDisclaimer: z
			.string()
			.optional()
			.default(defaultExamConfig.examDateDisclaimer),
	}),
	store: z.object({
		enabled: z.boolean(),
	}),
	homeView: z
		.object({
			showRecommendedChallenge: z.boolean().optional().default(false),
			showArticles: z.boolean().optional().default(false),
			showChallengeOfTheWeek: z.boolean().optional().default(false),
			masterySubheaderLabel: z.string().optional(),
			showRedeem: z.boolean().optional().default(false),
		})
		.optional()
		.nullable(),
	showStudyPackAsHome: z.boolean().optional().nullable(),
	reportView: reportViewConfigSchema.optional().nullable(),
	onboarding: z.array(onboardingStepSchema).optional().nullable(),
	navigation: navigationSchema,
	sessionView: sessionViewConfigSchema.optional().nullable(),
	theme: themeSchema,
});

export type CustomConfigType = z.infer<typeof customConfigTypeSchema>;

export type ConfigMap = Record<Apps, CustomConfigType>;
